import React, { useEffect, useState } from "react";
import {
  Box,
  StatusIndicator,
  StatusIndicatorProps,
} from "@amzn/awsui-components-react";
import { OmsProduct } from "../../lusca-search-api/generated-src";

// These UTC start times are the existing producer schedules, not completion estimates.
const scheduledHour: Record<OmsProduct, number> = {
  channels: 15,
  addon: 0,
  tvod: 11,
};
const dayMs = 24 * 60 * 60 * 1000;
const staleAfterMs = 36 * 60 * 60 * 1000;

export function reportTimestamp(value: string | number): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Refresh time unavailable";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(date);
}

export function scheduledRefresh(product: OmsProduct, now: number) {
  const last = new Date(now);
  last.setUTCHours(scheduledHour[product], 0, 0, 0);
  if (last.getTime() > now) last.setUTCDate(last.getUTCDate() - 1);
  return { last: last.getTime(), next: last.getTime() + dayMs };
}

export default function OMSReportSchedule({
  product,
  sourceRefreshedAt,
}: {
  product: OmsProduct;
  sourceRefreshedAt: string;
}) {
  const [now, setNow] = useState(Date.now);
  const refreshed = Date.parse(sourceRefreshedAt);
  const schedule = scheduledRefresh(product, now);
  const valid = Number.isFinite(refreshed);
  const stale = valid && now - refreshed >= staleAfterMs;
  const awaiting = valid && !stale && refreshed < schedule.last;
  useEffect(() => {
    let timer: number | undefined;
    function check() {
      const current = Date.now();
      setNow(current);
      const { next } = scheduledRefresh(product, current);
      const staleAt = refreshed + staleAfterMs;
      const boundary = staleAt > current ? Math.min(next, staleAt) : next;
      timer = window.setTimeout(check, Math.max(1, boundary - current));
    }
    function resume() {
      window.clearTimeout(timer);
      check();
    }
    check();
    window.addEventListener("focus", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [product, refreshed]);
  let label = "Report available";
  let type: StatusIndicatorProps.Type = "success";
  let caption =
    "Daily refresh. The scheduled time is the start; the report becomes available after completion.";
  if (!valid) {
    label = "Refresh time unavailable";
    type = "info";
  } else if (stale) {
    label = "Report is older than expected";
    type = "warning";
    caption =
      "You can still export this report. Scheduled times are refresh start times.";
  } else if (awaiting) {
    label = "Awaiting newer report";
    type = "pending";
    caption = `A refresh was scheduled for ${reportTimestamp(
      schedule.last
    )}. The latest completed report remains available.`;
  }
  return (
    <div className="oms-report-card__schedule">
      <div className="oms-report-card__availability" role="status">
        <StatusIndicator type={type}>{label}</StatusIndicator>
      </div>
      <dl className="oms-report-card__details">
        <div>
          <dt>Last refreshed</dt>
          <dd>
            {valid ? (
              <time dateTime={sourceRefreshedAt}>
                {reportTimestamp(sourceRefreshedAt)}
              </time>
            ) : (
              "Unavailable"
            )}
          </dd>
        </div>
        <div>
          <dt>Next scheduled refresh</dt>
          <dd>
            <time dateTime={new Date(schedule.next).toISOString()}>
              {reportTimestamp(schedule.next)}
            </time>
          </dd>
        </div>
      </dl>
      <Box color="text-body-secondary" fontSize="body-s">
        {caption}
      </Box>
    </div>
  );
}
